import { Link } from 'react-router-dom';
import { Package } from 'lucide-react';
import LargeNumberCard from './LargeNumberCard';
import PlateCard from './PlateCard';
import CarCard from './CarCard';
import PropertyCard from './PropertyCard';
import PriceDisplay from './PriceDisplay';
import PriceOrContact from '../PriceOrContact';
import useResponsivePlateWidth from '../../hooks/useResponsivePlateWidth';
import { cardFrame, riseDelay } from './frames';

/**
 * UnifiedStoreCard — renders items inside StorePage using the SAME Cyber-Luxe cards
 * used on marketplace listing pages, keeping the whole experience consistent.
 * Supports: phone_numbers, car_plates, auto (cars), real_estate, generic products.
 */
export default function UnifiedStoreCard({ product, store, isDarkMode, isAr, index = 0 }) {
  const type = product.item_type || store?.type;
  const merged = { ...product, store };
  const plateWidth = useResponsivePlateWidth({ mobile: 130, tablet: 200, desktop: 240 });

  if (type === 'phone_numbers') {
    return <LargeNumberCard item={merged} index={index} isDarkMode={isDarkMode} isAr={isAr} onClick={() => { window.location.assign(`/phone/${product.id}`); }} />;
  }
  if (type === 'car_plates') {
    return <PlateCard item={merged} index={index} isDarkMode={isDarkMode} isAr={isAr} plateWidth={plateWidth} testIdPrefix="store-plate" />;
  }
  if (type === 'auto') {
    return <CarCard item={merged} index={index} isDarkMode={isDarkMode} isAr={isAr} testIdPrefix="store-car" />;
  }
  if (type === 'real_estate') {
    return <PropertyCard item={merged} index={index} isDarkMode={isDarkMode} isAr={isAr} testIdPrefix="store-property" />;
  }

  return (
    <Link
      to={`/product/${product.id}`}
      className={`group relative block rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cgm-shimmer cgm-rise ${cardFrame(isDarkMode)}`}
      style={riseDelay(index)}
      data-testid={`store-product-${product.id}`}
    >
      {product.image ? (
        <div className="aspect-square overflow-hidden">
          <img src={product.image} alt={product.title || product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        </div>
      ) : (
        <div className={`aspect-square flex items-center justify-center ${isDarkMode ? 'cgm-stage-dark' : 'cgm-stage-light'}`}>
          <Package className="h-12 w-12 text-[#1E9BD9]/50" />
        </div>
      )}
      <div className="p-3 sm:p-4">
        <h3 className={`text-sm sm:text-base font-bold line-clamp-2 mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{product.title || product.name}</h3>
        {product.priceOnRequest ? (
          <PriceOrContact item={{ ...product, store }} variant="card" itemLabel={product.title || product.name || ''} />
        ) : (
          <PriceDisplay item={product} locale={isAr ? 'ar' : 'en'} size="sm" />
        )}
      </div>
    </Link>
  );
}
